import { ArrayUtils, engineComponent, StringUtils } from "neo-common";
import { Style } from "./Style";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { StyleManager } from "../../Managers/StyleManager";
import { ParticleElementAttributes } from "../Graphics/ParticleElement";

export interface IZoneOptions {
    type: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    innerWidth?: number;
    innerHeight?: number;
    radius?: number;
    minRadius?: number;
    radiusB?: number;
    minRadiusB?: number;
    innerRadius?: number;
    minInnerRadius?: number;
    innerRadiusB?: number;
    minInnerRadiusB?: number;
    innerX?: number;
    innerY?: number;
    angle?: number;
    startAngle?: number;
}

export interface ICoordinateOptions1D {
    value?: number;
    randomValue?: number;
    relative?: boolean;
}

export interface ICoordinateOptions2D {
    x?: number;
    y?: number;
    randomX?: number;
    randomY?: number;
    relative?: boolean;
}

@engineComponent(ComponentTypes.Style)
// TODO add comments
export class ParticleStyle extends Style {
    // value in percentage for creating reserv in display list to pooling particles
    static ElementsReserve: number = 0.5;

    // names for zones
    static ZoneRectangle: string = "Rectangle";
    static ZoneLine: string = "Line";
    static ZoneEllipse: string = "Ellipse";
    static ZoneTarget: string = "Target";
    static ZoneDynamic: string = "Dynamic";

    static NodeCount: string = "Count";
    static NodeLife: string = "Life";
    static NodeImages: string = "Images";
    static NodePosition: string = "Position";
    static NodePath: string = "Path";
    static NodeScale: string = "Scale";
    static NodeRotation: string = "Rotation";
    static NodeAlpha: string = "Alpha";
    static NodeValue: string = "Value";

    // TODO possible memory leak
    node: Element;

    private _count: number;
    private _time: number;
    private _minLife: number;
    private _maxLife: number;
    private _images: Array<string>;
    private _randomStartFrame: boolean;
    private _subParticleStyle: string;
    private _positionZone: IZoneOptions;

    private _pathEase: string;
    private _pathValues: Array<ICoordinateOptions2D>;
    private _pathKeyTimes: Array<number>;

    private _scaleEase: string;
    private _scaleValues: Array<ICoordinateOptions2D>;
    private _scaleKeyTimes: Array<number>;

    private _rotationEase: string;
    private _rotationValues: Array<ICoordinateOptions1D>;
    private _rotationKeyTimes: Array<number>;

    private _alphaEase: string;
    private _alphaValues: Array<ICoordinateOptions1D>;
    private _alphaKeyTimes: Array<number>;

    private _tint: number;

    constructor() {
        super();

        this._count = 1;
        this._time = 60;
        this._minLife = 60;
        this._maxLife = 120;
        this._images = [];
        this._randomStartFrame = false;
        this._subParticleStyle = "";
        this._positionZone = {
            type: ParticleStyle.ZoneRectangle,
            x: 0,
            y: 0,
            innerX: 0,
            innerY: 0,
            width: StageController.Width,
            height: StageController.Height,
            innerWidth: -1,
            innerHeight: -1,
            radius: 0,
            minRadius: 0,
            radiusB: 0,
            minRadiusB: 0,
            innerRadius: 0,
            minInnerRadius: 0,
            innerRadiusB: 0,
            minInnerRadiusB: 0,
            angle: 360,
            startAngle: 0, //used to prevent container rotation deformation with ellipse
        };
        this._pathEase = "linear";
        this._pathValues = [];
        this._pathKeyTimes = null;

        this._scaleEase = "linear";
        this._scaleValues = [];
        this._scaleKeyTimes = null;

        this._rotationEase = "linear";
        this._rotationValues = [];
        this._rotationKeyTimes = null;

        this._alphaEase = "linear";
        this._alphaValues = [];
        this._alphaKeyTimes = null;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element) {
        this.node = node;

        super.create(node);

        if (node.hasAttribute(ParticleElementAttributes.EXTEND_STYLE)) {
            let extendStyle: ParticleStyle = <ParticleStyle>(
                StyleManager.Styles[node.getAttribute(ParticleElementAttributes.EXTEND_STYLE)]
            );
            if (extendStyle != null && extendStyle.node != null) {
                this.parseChildrenNodes(extendStyle.node);
            }
        }

        this.parseChildrenNodes(node);
    }

    private parseChildrenNodes(node: Element): void {
        let next: Element = node.firstElementChild;
        while (next != null) {
            switch (next.nodeName) {
                case ParticleStyle.NodeCount:
                    this.initializeCount(next);
                    break;
                case ParticleStyle.NodeLife:
                    this.initializeLife(next);
                    break;
                case ParticleStyle.NodeImages:
                    this.initializeImages(next);
                    break;
                case ParticleStyle.NodePosition:
                    this.initializePosition(next);
                    break;
                case ParticleStyle.NodePath:
                    this.initializePath(next);
                    break;
                case ParticleStyle.NodeScale:
                    this.initializeScale(next);
                    break;
                case ParticleStyle.NodeRotation:
                    this.initializeRotation(next);
                    break;
                case ParticleStyle.NodeAlpha:
                    this.initializeAlpha(next);
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown node in particle style " + next.nodeName);
                    }
            }
            next = next.nextElementSibling;
        }
    }

    get count(): number {
        return this._count;
    }

    get time(): number {
        return this._time;
    }

    get minLife(): number {
        return this._minLife;
    }

    get maxLife(): number {
        return this._maxLife;
    }

    get images(): Array<string> {
        return this._images;
    }

    get randomStartFrame(): boolean {
        return this._randomStartFrame;
    }

    get subParticleStyle(): string {
        return this._subParticleStyle;
    }

    get positionZone(): IZoneOptions {
        return this._positionZone;
    }

    get pathValues(): Array<ICoordinateOptions2D> {
        return this._pathValues;
    }

    get scaleValues(): Array<ICoordinateOptions2D> {
        return this._scaleValues;
    }

    get rotationValues(): Array<ICoordinateOptions1D> {
        return this._rotationValues;
    }

    get alphaValues(): Array<ICoordinateOptions1D> {
        return this._alphaValues;
    }

    get pathEase(): string {
        return this._pathEase;
    }

    get scaleEase(): string {
        return this._scaleEase;
    }

    get rotationEase(): string {
        return this._rotationEase;
    }

    get alphaEase(): string {
        return this._alphaEase;
    }

    get pathKeyTimes(): Array<number> {
        return this._pathKeyTimes;
    }

    get scaleKeyTimes(): Array<number> {
        return this._scaleKeyTimes;
    }

    get rotationKeyTimes(): Array<number> {
        return this._rotationKeyTimes;
    }

    get alphaKeyTimes(): Array<number> {
        return this._alphaKeyTimes;
    }

    calculateElements(): number {
        let elementsPerSecond: number = (StageController.FPS / this._time) * this._count;
        let elementsWithLife: number = (this._maxLife / StageController.FPS) * elementsPerSecond;
        return elementsWithLife;
    }

    /**
     * Initialize elements per second.
     * Count - how much elements
     * Time - time when specified count of elements is generated
     *
     * Example:
     *  <Count Count="8" Time="2"/>
     * @param node
     */
    private initializeCount(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_COUNT)) {
            this._count = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_COUNT));
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_TIME)) {
            this._time = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_TIME));
        }
    }

    /**
     * Example:
     *  <Life Min="500" Max="1000"/>
     * @param node
     */
    private initializeLife(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN)) {
            this._minLife = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_MIN));
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_MAX)) {
            this._maxLife = parseInt(node.getAttribute(ParticleStyleAttributes.ATTR_MAX));
        }

        if (this._minLife > this._maxLife) {
            this._maxLife = this._minLife;
        }
    }

    /**
     * Example:
     *  <Images Images="GFX_particle1;GFX_particle2;..."/>
     * @param node
     */
    private initializeImages(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_FROM_FRAME)) {
            this._images = node.getAttribute(ParticleStyleAttributes.ATTR_FROM_FRAME).split(";");

            for (let i: number = 0; i < this._images.length; i++) {
                // TODO 0000
                this._images[i] = this._images[i] + "0000";
            }
        } else if (node.hasAttribute(ParticleStyleAttributes.ATTR_IMAGES)) {
            this._images = node.getAttribute(ParticleStyleAttributes.ATTR_IMAGES).split(";");
        }

        if (node.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_START_FRAME)) {
            this._randomStartFrame = StringUtils.parseBoolean(
                node.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_START_FRAME),
            );
        }

        if (node.hasAttribute(ParticleStyleAttributes.ATTR_SUB_PARTICLE_STYLE)) {
            this._subParticleStyle = node.getAttribute(ParticleStyleAttributes.ATTR_SUB_PARTICLE_STYLE);
        }
    }

    /**
     * Bornplace of elements.
     * Example:
     *  <Position Type="Rectangle" X="0" Y="-100" Width="288" Height="1080"/>
     * @param node
     */
    private initializePosition(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_TYPE)) {
            this._positionZone.type = node.getAttribute(ParticleStyleAttributes.ATTR_TYPE);
            switch (this._positionZone.type) {
                case ParticleStyle.ZoneRectangle:
                    // TODO separate method get Zone
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                        this._positionZone.x = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_X));
                        this._positionZone.innerX = this._positionZone.x;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                        this._positionZone.y = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_Y));
                        this._positionZone.innerY = this._positionZone.y;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_X)) {
                        this._positionZone.innerX = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_X));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_Y)) {
                        this._positionZone.innerY = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_Y));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_WIDTH)) {
                        this._positionZone.width = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_WIDTH));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_HEIGHT)) {
                        this._positionZone.height = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_HEIGHT));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_WIDTH)) {
                        this._positionZone.innerWidth = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_WIDTH),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_HEIGHT)) {
                        this._positionZone.innerHeight = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_HEIGHT),
                        );
                    }
                    break;
                case ParticleStyle.ZoneTarget:
                    this._positionZone.width = 0;
                    this._positionZone.height = 0;

                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_WIDTH)) {
                        this._positionZone.width = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_WIDTH));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_HEIGHT)) {
                        this._positionZone.height = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_HEIGHT));
                    }

                    break;
                case ParticleStyle.ZoneDynamic:
                    break;
                case ParticleStyle.ZoneLine:
                    // TODO
                    break;
                case ParticleStyle.ZoneEllipse:
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                        this._positionZone.x = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_X));
                        this._positionZone.innerX = this._positionZone.x;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                        this._positionZone.y = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_Y));
                        this._positionZone.innerY = this._positionZone.y;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_X)) {
                        this._positionZone.innerX = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_X));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_Y)) {
                        this._positionZone.innerY = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_Y));
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_RADIUS)) {
                        let radius = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_RADIUS));
                        //set all attributes by radius, if we have only circle with no minRadius
                        this._positionZone.radius = radius;
                        this._positionZone.radiusB = radius;
                        this._positionZone.minRadius = radius;
                        this._positionZone.minRadiusB = radius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS)) {
                        this._positionZone.minRadius = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS),
                        );
                        this._positionZone.minRadiusB = this._positionZone.minRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_RADIUS_B)) {
                        this._positionZone.radiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_RADIUS_B),
                        );
                        this._positionZone.minRadiusB = this._positionZone.radiusB;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS_B)) {
                        this._positionZone.minRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_RADIUS_B),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS)) {
                        let innerRadius = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS));
                        this._positionZone.innerRadius = innerRadius;
                        this._positionZone.innerRadiusB = innerRadius;
                        this._positionZone.minInnerRadius = innerRadius;
                        this._positionZone.minInnerRadiusB = innerRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS)) {
                        this._positionZone.minInnerRadius = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS),
                        );
                        this._positionZone.minInnerRadiusB = this._positionZone.minInnerRadius;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS_B)) {
                        this._positionZone.innerRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_INNER_RADIUS_B),
                        );
                        this._positionZone.minInnerRadiusB = this._positionZone.innerRadiusB;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS_B)) {
                        this._positionZone.minInnerRadiusB = parseFloat(
                            node.getAttribute(ParticleStyleAttributes.ATTR_MIN_INNER_RADIUS_B),
                        );
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_START_ANGLE)) {
                        let startAngle = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_START_ANGLE));
                        this._positionZone.startAngle = startAngle;
                    }
                    if (node.hasAttribute(ParticleStyleAttributes.ATTR_ANGLE)) {
                        let angle = parseFloat(node.getAttribute(ParticleStyleAttributes.ATTR_ANGLE));
                        this._positionZone.angle = angle;
                    }
                    break;
                default:
                    if (DEBUG) {
                        this.log("WARNING unknown zone " + this._positionZone.type);
                    }
            }
        }
    }

    /**
     * Initialize movement from defined start points to end points with possible random range.
     * Example:
     *  <Path Ease="circOut" KeyTimes="0;0.4;1">
     *      <Value X="0" Y="0"/>
     *      <Value X="0" Y="-100"/>
     *      <Value X="100" Y="-100" RandomX="100" RandomY="-100"/>
     *  </Path>
     * @param node
     */
    private initializePath(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_EASE)) {
            this._pathEase = node.getAttribute(ParticleStyleAttributes.ATTR_EASE);
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES)) {
            this._pathKeyTimes = ArrayUtils.numberArray(node.getAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES));
        }
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                let path: ICoordinateOptions2D = { x: null, y: null, randomX: null, randomY: null, relative: false };
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                    path.x = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_X));
                } else {
                    path.x = 0;
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                    path.y = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_Y));
                } else {
                    path.y = 0;
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_X)) {
                    path.randomX = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_X));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y)) {
                    path.randomY = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RELATIVE)) {
                    path.relative = StringUtils.parseBoolean(next.getAttribute(ParticleStyleAttributes.ATTR_RELATIVE));
                }
                this._pathValues.push(path);
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Scale of object.
     * Example:
     *  <Scale Ease="circOut" KeyTimes="0;0.75;1">
     *      <Value X="0.5" Y="0.5" RandomX="0.2"/> (X between 0.5 and 0.7, Y=0.5)
     *      <Value Value="0.6" RandomValue="0.2"/> (both identical, between 0.6 and 0.8)
     *      <Value  X="0.5" Y="0.5" RandomX="0.2" RandomY="0.2"/> (both independent between 0.5 and 0.7)
     *  </Scale>
     * @param node
     */
    private initializeScale(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_EASE)) {
            this._scaleEase = node.getAttribute(ParticleStyleAttributes.ATTR_EASE);
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES)) {
            this._scaleKeyTimes = ArrayUtils.numberArray(node.getAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES));
        }
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                let coordinates: ICoordinateOptions2D = {
                    x: null,
                    y: null,
                    randomX: null,
                    randomY: null,
                    relative: false,
                };

                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUES)) {
                    let arr = ArrayUtils.numberArray(next.getAttribute(ParticleStyleAttributes.ATTR_VALUES));
                    if (arr.length > 1) {
                        coordinates.x = (arr[1] - arr[0]) / 2 + arr[0];
                        coordinates.randomX = arr[1] - arr[0];
                    } else {
                        coordinates.x = arr[0];
                    }
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE)) {
                    coordinates.x = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM)) {
                    coordinates.randomX = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM));
                }

                if (next.hasAttribute(ParticleStyleAttributes.ATTR_X)) {
                    coordinates.x = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_X));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_X)) {
                    coordinates.randomX = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_X));
                }

                if (next.hasAttribute(ParticleStyleAttributes.ATTR_Y)) {
                    coordinates.y = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_Y));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y)) {
                    coordinates.randomY = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_RANDOM_Y));
                }

                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RELATIVE)) {
                    coordinates.relative = StringUtils.parseBoolean(
                        next.getAttribute(ParticleStyleAttributes.ATTR_RELATIVE),
                    );
                }

                this._scaleValues.push(coordinates);
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Rotations of object in degrees.
     * Example:
     *  <Rotation Ease="circOut" KeyTimes="0;0.25;1">
     *      <Value Values="1"/>
     *      <Value Values="-30;-20"/>
     *      <Value Values="30;45"/>
     *  </Rotation>
     * @param node
     */
    private initializeRotation(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_EASE)) {
            this._rotationEase = node.getAttribute(ParticleStyleAttributes.ATTR_EASE);
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES)) {
            this._rotationKeyTimes = ArrayUtils.numberArray(node.getAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES));
        }
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                let coordinates: ICoordinateOptions1D = { value: null, randomValue: null, relative: false };
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUES)) {
                    let arr = ArrayUtils.numberArray(next.getAttribute(ParticleStyleAttributes.ATTR_VALUES));
                    if (arr.length > 1) {
                        coordinates.value = (arr[1] - arr[0]) / 2 + arr[0];
                        coordinates.randomValue = arr[1] - arr[0];
                    } else {
                        coordinates.value = arr[0];
                    }
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE)) {
                    coordinates.value = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM)) {
                    coordinates.randomValue = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RELATIVE)) {
                    coordinates.relative = StringUtils.parseBoolean(
                        next.getAttribute(ParticleStyleAttributes.ATTR_RELATIVE),
                    );
                }

                this._rotationValues.push(coordinates);
            }
            next = next.nextElementSibling;
        }
    }

    /**
     * Example:
     *  <Alpha Ease="linear" KeyTimes="0;1">
     *      <Value Value="0.8" ValueRandom="0.1"/>
     *      <Value Value="1"/>
     *  </Alpha>
     * @param node The Alpha node
     */
    private initializeAlpha(node: Element): void {
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_EASE)) {
            this._alphaEase = node.getAttribute(ParticleStyleAttributes.ATTR_EASE);
        }
        if (node.hasAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES)) {
            this._alphaKeyTimes = ArrayUtils.numberArray(node.getAttribute(ParticleStyleAttributes.ATTR_KEY_TIMES));
        }
        let next: Element = node.firstElementChild;
        while (next != null) {
            if (next.nodeName === ParticleStyle.NodeValue) {
                let coordinates: ICoordinateOptions1D = { value: null, randomValue: null, relative: false };
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE)) {
                    coordinates.value = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM)) {
                    coordinates.randomValue = parseFloat(next.getAttribute(ParticleStyleAttributes.ATTR_VALUE_RANDOM));
                }
                if (next.hasAttribute(ParticleStyleAttributes.ATTR_RELATIVE)) {
                    coordinates.relative = StringUtils.parseBoolean(
                        next.getAttribute(ParticleStyleAttributes.ATTR_RELATIVE),
                    );
                }
                this._alphaValues.push(coordinates);
            }
            next = next.nextElementSibling;
        }
    }
}

const ParticleStyleAttributes = {
    ATTR_COUNT: "Count",
    ATTR_TIME: "Time",
    ATTR_MIN: "Min",
    ATTR_MAX: "Max",
    ATTR_FROM_FRAME: "FromFrame",
    ATTR_IMAGES: "Images",
    ATTR_TYPE: "Type",
    ATTR_X: "X",
    ATTR_Y: "Y",
    ATTR_RELATIVE: "Relative",
    ATTR_INNER_X: "InnerX",
    ATTR_INNER_Y: "InnerY",
    ATTR_RADIUS: "Radius",
    ATTR_MIN_RADIUS: "MinRadius",
    ATTR_RADIUS_B: "RadiusB",
    ATTR_MIN_RADIUS_B: "MinRadiusB",
    ATTR_INNER_RADIUS: "InnerRadius",
    ATTR_MIN_INNER_RADIUS: "MinInnerRadius",
    ATTR_INNER_RADIUS_B: "InnerRadiusB",
    ATTR_MIN_INNER_RADIUS_B: "MinInnerRadiusB",
    ATTR_ANGLE: "Angle",
    ATTR_START_ANGLE: "StartAngle",
    ATTR_RANDOM_X: "RandomX",
    ATTR_RANDOM_Y: "RandomY",
    ATTR_VALUE_RANDOM: "RandomValue",
    ATTR_WIDTH: "Width",
    ATTR_HEIGHT: "Height",
    ATTR_INNER_WIDTH: "InnerWidth",
    ATTR_INNER_HEIGHT: "InnerHeight",
    ATTR_VALUES: "Values",
    ATTR_VALUE: "Value",
    ATTR_RANDOM_START_FRAME: "RandomStartFrame",
    ATTR_SUB_PARTICLE_STYLE: "SubParticleStyle",

    ATTR_EASE: "Ease",
    ATTR_KEY_TIMES: "KeyTimes",
    ATTR_RANDOM_PASSES: "RandomPasses",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Styles/ParticleStyle.ts
 **/